import React, { useEffect, useMemo, useRef } from 'react';
import Otto from '../otto/Otto';
import './sendBriefFlight.css';

// One-shot send-brief flight, fired when an internal admin sends a
// brief. Otto (flying pose, carrying the brief envelope) launches from
// the Send button and flies diagonally up and off-screen; the parent
// shows the success confirmation as he goes. The parent mounts
// <BriefSendFlight /> when it wants the flight, passes an `origin`
// (viewport coords of the Send button's centre, captured at click
// time), and unmounts when onComplete fires.
//
// Pure CSS animation, trajectory driven by CSS custom properties set
// inline. Total duration 850ms; we wait 950ms before signalling
// complete so Otto has time to finish fading. Reduced-motion: no
// flight, fire onComplete immediately (confirmation still shows).

// Slowed twice. First from 850/950ms, because the original was over before you
// registered Otto had launched. Then again to ~2.3s (Rob, 2026-08-14: "he is a
// bit small and quick at the moment") — sending a brief is the most consequential
// button on the page and the send-off should have time to read.
export const FLIGHT_TOTAL_MS = 2400;
const ANIMATION_DURATION_MS = 2300;
const FLY_X = 620;
const FLY_Y = -380;

// Otto's size, up from 64 for the same reason. DERIVED centring below rather
// than the hardcoded 32 that used to sit beside it — those two numbers have to
// stay in step, and one of them being a literal is how they stop being.
const OTTO_SIZE = 104;

// Wing beats. The flight is ~2.3s and a beat is 290ms, so 8 carries him the
// whole way with a little in hand; `both` holds the last frame rather than
// snapping back to the static pose while he is still on screen.
const WING_BEAT_MS = 290;
const WING_BEATS = Math.ceil(ANIMATION_DURATION_MS / WING_BEAT_MS);

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function BriefSendFlight({ origin, onComplete }) {
  // Snapshot the reduced-motion preference once so a mid-flight
  // OS-preference change doesn't flip behaviour halfway through.
  const reducedMotion = useMemo(prefersReducedMotion, []);

  // Capture onComplete in a ref so the unmount effect doesn't re-fire
  // if the parent re-renders with a fresh callback.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; });

  useEffect(() => {
    if (reducedMotion) {
      // Honour the preference: no plane, no waiting around.
      onCompleteRef.current?.();
      return undefined;
    }
    const timer = setTimeout(() => onCompleteRef.current?.(), FLIGHT_TOTAL_MS);
    return () => clearTimeout(timer);
  }, [reducedMotion]);

  if (reducedMotion) return null;

  // Fallback to viewport centre if the caller didn't pin an origin
  // (e.g. the Send button unmounted before we snapshotted its
  // position). Better than nothing.
  const safeOrigin = origin ?? {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  };

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: safeOrigin.x - OTTO_SIZE / 2, // centre him on the origin
        top: safeOrigin.y - OTTO_SIZE / 2,
        width: 0,
        height: 0,
        pointerEvents: 'none',
        zIndex: 100,
      }}>
      <div
        className="brief-send-otto"
        style={{
          '--fly-x': `${FLY_X}px`,
          '--fly-y': `${FLY_Y}px`,
          // Otto's flying pose already leans into the direction of
          // travel, so no extra rotation.
          '--plane-rotation': '0deg',
          // The wing beat is driven from here so the count stays tied to the
          // flight length rather than being a number guessed in the CSS.
          '--wing-beat': `${WING_BEAT_MS}ms`,
          '--wing-beats': WING_BEATS,
          animationDuration: `${ANIMATION_DURATION_MS}ms`,
        }}>
        <Otto pose="flying" size={OTTO_SIZE} ariaLabel="" />
      </div>
    </div>
  );
}
